import { type CatalogType, isCatalogType } from "@/lib/site-catalog-types";

export const SPOTLIGHT_RESULT_LIMIT = 12;
export const SPOTLIGHT_DEBOUNCE_MS = 250;

export interface SpotlightSearchResult {
  type: CatalogType;
  title: string;
  description: string;
  /** A canonical HTML path that already includes the requested locale. */
  localizedPath: string;
}

export interface SpotlightSearchResponse {
  total: number;
  results: SpotlightSearchResult[];
}

export interface SpotlightResultGroup {
  type: CatalogType;
  results: SpotlightSearchResult[];
}

interface SearchHandlers {
  onSuccess: (response: SpotlightSearchResponse) => void;
  onError: () => void;
}

interface ActiveSearch {
  controller: AbortController;
  timer: ReturnType<typeof setTimeout>;
}

const SEARCH_ENDPOINT = "/api/v1/search";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPathForLocale(path: string, locale: string): boolean {
  const root = `/${locale}`;
  return path === root || path.startsWith(`${root}/`);
}

function parseResult(
  value: unknown,
  locale: string
): SpotlightSearchResult | null {
  if (!isRecord(value)) {
    return null;
  }
  if (!isCatalogType(value.type)) {
    return null;
  }
  if (
    typeof value.title === "string" &&
    typeof value.description === "string" &&
    typeof value.localizedPath === "string" &&
    isPathForLocale(value.localizedPath, locale)
  ) {
    return {
      type: value.type,
      title: value.title,
      description: value.description,
      localizedPath: value.localizedPath,
    };
  }
  return null;
}

function parseResponse(
  value: unknown,
  locale: string
): SpotlightSearchResponse {
  if (
    !isRecord(value) ||
    value.locale !== locale ||
    typeof value.total !== "number" ||
    !Number.isSafeInteger(value.total) ||
    value.total < 0 ||
    !Array.isArray(value.results)
  ) {
    throw new Error("Invalid Spotlight search response.");
  }

  const results = value.results.map((result) => parseResult(result, locale));
  if (results.some((result) => result === null)) {
    throw new Error("Invalid Spotlight search result.");
  }
  return {
    total: value.total,
    results: (results as SpotlightSearchResult[]).slice(
      0,
      SPOTLIGHT_RESULT_LIMIT
    ),
  };
}

export function buildSpotlightSearchUrl(query: string, locale: string): string {
  const params = new URLSearchParams({
    q: query.trim(),
    locale,
    limit: String(SPOTLIGHT_RESULT_LIMIT),
  });
  return `${SEARCH_ENDPOINT}?${params.toString()}`;
}

export async function fetchSpotlightResults(
  query: string,
  locale: string,
  signal: AbortSignal,
  fetcher: typeof fetch = fetch
): Promise<SpotlightSearchResponse> {
  const response = await fetcher(buildSpotlightSearchUrl(query, locale), {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Spotlight search failed with ${response.status}.`);
  }
  return parseResponse((await response.json()) as unknown, locale);
}

/**
 * One debounced request lane for the palette.
 *
 * Aborting saves work when the browser honors it. The sequence check is separate and
 * load-bearing: a completed response is ignored even when a fetch implementation
 * resolves after its signal was aborted.
 */
export class SpotlightSearchController {
  private active: ActiveSearch | null = null;
  private sequence = 0;
  private readonly delay: number;
  private readonly fetcher: typeof fetch;

  constructor(fetcher: typeof fetch = fetch, delay = SPOTLIGHT_DEBOUNCE_MS) {
    this.fetcher = fetcher;
    this.delay = delay;
  }

  search(query: string, locale: string, handlers: SearchHandlers): void {
    this.cancel();
    const sequence = this.sequence;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      return this.run(query, locale, sequence, controller, handlers);
    }, this.delay);
    this.active = { controller, timer };
  }

  cancel(): void {
    this.sequence += 1;
    if (!this.active) {
      return;
    }
    clearTimeout(this.active.timer);
    this.active.controller.abort();
    this.active = null;
  }

  private async run(
    query: string,
    locale: string,
    sequence: number,
    controller: AbortController,
    handlers: SearchHandlers
  ): Promise<void> {
    try {
      const response = await fetchSpotlightResults(
        query,
        locale,
        controller.signal,
        this.fetcher
      );
      if (sequence === this.sequence) {
        handlers.onSuccess(response);
      }
    } catch {
      if (sequence === this.sequence) {
        handlers.onError();
      }
    } finally {
      if (sequence === this.sequence) {
        this.active = null;
      }
    }
  }
}

/** Preserve server relevance order while giving cmdk translated type headings. */
export function groupSpotlightResults(
  results: SpotlightSearchResult[]
): SpotlightResultGroup[] {
  const groups: SpotlightResultGroup[] = [];
  for (const result of results) {
    const previous = groups.at(-1);
    if (previous?.type === result.type) {
      previous.results.push(result);
    } else {
      groups.push({ type: result.type, results: [result] });
    }
  }
  return groups;
}
